import api from "../../../../services/api";

/**
 * Get department detail by UUID
 * @param {string} department_uuid - Department UUID
 * @returns {Promise<Object>} API response with department detail
 */
export const getDepartmentDetail = async (department_uuid) => {
  if (!department_uuid) {
    throw new Error("department_uuid is required");
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(String(department_uuid).trim())) {
    throw new Error("Invalid department_uuid format");
  }

  try {
    const trimmedUuid = department_uuid.trim();
    const response = await api.get(`/v1/department/${trimmedUuid}`);
    const responseData = response.data;

    if (!responseData) {
      throw new Error("Invalid response from server");
    }

    const statusCode =
      responseData.status_code || responseData.statusCode || response?.status || 200;
    const msgCode = responseData.msg_code || responseData.msgCode || [];
    const department = responseData.body || responseData;

    if (statusCode >= 200 && statusCode < 300 && department) {
      return {
        status_code: statusCode,
        msg_code: msgCode,
        request_id: responseData.request_id || responseData.requestId,
        body: department,
        meta: responseData.meta || null,
      };
    }

    const errorMessage =
      msgCode.length > 0
        ? msgCode[0].description || msgCode[0].code || "An error occurred while fetching department."
        : "An error occurred while fetching department.";

    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: statusCode,
    };

    throw error;
  } catch (error) {
    console.warn("API call failed, falling back to mock data:", error.message);

    try {
      const mockResponse = await fetch("/mock-department-list.json");
      const mockData = await mockResponse.json();

      const mockDepartment = (mockData.body || []).find(
        (dept) => (dept.departmentUuid || dept.department_uuid) === department_uuid.trim()
      );

      if (!mockDepartment) {
        return {
          status_code: 404,
          msg_code: [
            {
              code: "DEPARTMENT_NOT_FOUND",
              description: "Department not found.",
            },
          ],
          request_id: `mock-${Date.now()}`,
          body: null,
          meta: null,
        };
      }

      return {
        status_code: 200,
        msg_code: [
          {
            code: "DEPARTMENT_DETAIL_RETRIEVED",
            description: "Department detail retrieved successfully.",
          },
        ],
        request_id: mockData.request_id || `mock-${Date.now()}`,
        body: {
          departmentUuid: mockDepartment.departmentUuid || mockDepartment.department_uuid,
          departmentName: mockDepartment.departmentName || mockDepartment.department_name,
          departmentTag: mockDepartment.departmentTag || mockDepartment.tags || [],
          description: mockDepartment.description || "",
          status: mockDepartment.status || "ACTIVE",
          groupUuid: mockDepartment.groupUuid || `group-${mockDepartment.departmentUuid || mockDepartment.department_uuid}`,
          ownerUuid: mockDepartment.ownerUuid || mockDepartment.owner_uuid || null,
          ownerName: mockDepartment.ownerName || mockDepartment.owner_name || "",
          ownerPhotoUrl: mockDepartment.ownerPhotoUrl || mockDepartment.photo_url || null,
          ownerRole: mockDepartment.ownerRole || "Department Admin",
          userCount: mockDepartment.userCount ?? mockDepartment.total_active_members ?? 0,
          knowledgeBaseCount: mockDepartment.knowledgeBaseCount ?? mockDepartment.total_kbu ?? 0,
          knowledgeBase: mockDepartment.knowledgeBase || mockDepartment.knowledgeBases || mockDepartment.knowledge_bases || [],
          createdAt: mockDepartment.createdAt || mockDepartment.created_at,
          updatedAt: mockDepartment.updatedAt || mockDepartment.updated_at || mockDepartment.createdAt || mockDepartment.created_at || null,
          agentWorkflows: mockDepartment.agentWorkflows ?? 0,
          overrides: mockDepartment.overrides ?? 0,
          feedbackItems: mockDepartment.feedbackItems ?? 0,
        },
        meta: null,
      };
    } catch (mockError) {
      console.error("Error fetching mock department detail:", mockError);
      return {
        status_code: 500,
        msg_code: [
          {
            code: "DEPARTMENT_DETAIL_ERROR",
            description: "An unexpected error occurred while fetching department detail.",
          },
        ],
        body: null,
        meta: null,
      };
    }
  }
};

